import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import { IconButton, Slider, Button } from '@mui/material';
import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ICameraScanner } from '../Abstraction/ICameraScanner';
import { useOperation } from '../../Hooks/OperationContext';
import "./ScannerPreview.scss"

interface ICameraScannerPreviewProps {
    info?: ReactNode;
    scanner: ICameraScanner;
    isActive?: boolean;
    useCanvas?: boolean;
}

const SPEED = 200; //pixel / sec
const LINE_SIZE = 4;
const PADDING = 0.1;

export default function CameraScannerPreview(props: ICameraScannerPreviewProps) {

    const [needRefresh, setNeedRefresh] = useState(false);

    const [capabilities, setCapabilities] = useState(props.scanner.getCapabilities());

    const canvasRef = useRef<HTMLCanvasElement>()

    const operation = useOperation();

    const refresh = () => setNeedRefresh(!needRefresh);

    const updateCaps = () => {
        const value = props.scanner.getCapabilities();
        console.log(JSON.stringify(value));
        setCapabilities(value);
    };

    const video = props.scanner.getVideo();

    const drawFrame = () => {

        if (canvasRef.current) {

            if (!canvasRef.current["@startTime"])
                canvasRef.current["@startTime"] = new Date().getTime();

            const curTime = (new Date().getTime() - canvasRef.current["@startTime"]) as number;

            var ctx = canvasRef.current.getContext("2d");

            const curSpeed = (canvasRef.current.height / SPEED) * 1000;

            const codeArea = {
                x: canvasRef.current.width * PADDING,
                y: canvasRef.current.height * PADDING,
                w: canvasRef.current.width - canvasRef.current.width * PADDING * 2,
                h: canvasRef.current.height - canvasRef.current.height * PADDING * 2,
            };

            let curY = (curTime % curSpeed) / curSpeed * (codeArea.h * 2);
            if (curY > codeArea.h)
                curY = codeArea.h - (curY - codeArea.h);

            ctx.drawImage(video, 0, 0, canvasRef.current.width, canvasRef.current.height);

            ctx.save();
            ctx.fillStyle = "rgba(255,0,0, 0.7)";
            ctx.fillRect(codeArea.x, codeArea.y + curY - LINE_SIZE / 2, codeArea.w, LINE_SIZE);
            ctx.restore();

            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, codeArea.x, canvasRef.current.height);
            ctx.rect(codeArea.x + codeArea.w, 0, canvasRef.current.width - (codeArea.x + codeArea.w), canvasRef.current.height);
            ctx.rect(codeArea.x, 0, codeArea.w, codeArea.y);
            ctx.rect(codeArea.x, codeArea.y + codeArea.h, codeArea.w, canvasRef.current.height - (codeArea.y + codeArea.h));
            ctx.clip();
            ctx.fillStyle = "rgba(0,0,0, 0.4)";
            ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.restore();

            ctx.strokeStyle = "rgba(0,0,0, 0.5)";
            ctx.lineWidth = 2;
            ctx.strokeRect(codeArea.x, codeArea.y, codeArea.w, codeArea.h);
        }

        if (!video.paused)
            requestAnimationFrame(drawFrame);
    }

    const onScannerReady = useMemo(() => () => {

        console.debug("onScannerReady");

        if (canvasRef.current && video) {
            canvasRef.current.width = video.videoWidth;
            canvasRef.current.height = video.videoHeight;

            requestAnimationFrame(drawFrame);
        }

        updateCaps();

    },[]);
     
    useEffect(() => {

        props.scanner.onScannerReady.add(onScannerReady);

    }, [props.scanner]);


    const onCancelScan = () => {
        props.scanner.stopScanAsync();
    }

    const onFocusChanged = (value: number) => {
        if (value == -1)
            operation.showAlert("Focus automatico", "info", 3000);

        props.scanner.updateFocusAsync(value);
    }

    const toggleFlash = () => {
        props.scanner.updateFlashAsync(!props.scanner.isFlashOn);
        refresh();
    }

    const switchCamera = () => {
        props.scanner.switchCameraAsync();
    }

    return (
        <div className="scan-preview camera hidden" onClick={ev => ev.preventDefault()}>
            <div className="actions">

                <div className="focus-container">
                    {capabilities?.focus ?
                    <Fragment>
                        <span>FOCUS</span>
                        <Slider
                            className="focus"
                            size="small"
                            min={-1}
                            max={2}
                            step={0.1}
                            defaultValue={props.scanner.focus}
                            onChange={(e, value) => onFocusChanged(value as number)}
                            aria-label="Small"
                            valueLabelDisplay="auto"
                        />
                    </Fragment>
                    : null}
                </div>

                {props.scanner.cameras?.length > 1 ?
                    <IconButton onClick={() => switchCamera()} >
                        <CameraswitchIcon />
                    </IconButton>
                    : null}
                {capabilities?.flash ?
                    <IconButton className={props.scanner.isFlashOn ? "active" : ""} onClick={() => toggleFlash()} >
                        <LightbulbIcon />
                    </IconButton>
                    : null}
            </div>

            <div className="main">
                {props.useCanvas ? <canvas ref={canvasRef} id="preview" /> :
                    <video id="preview">
                    </video>
                }
            </div>
            <div className="info">{props.info} </div>
            <Button onClick={onCancelScan} className="cancel-scan">Chiudi</Button>

        </div>
    );
}